'use client'
import { StoreProduct } from '@medusajs/types'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { isEqual } from 'lodash'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'
import { bagelFatOne } from '@/utilities/fontImport'
import { cn } from '@/utilities/ui'
import { isDefaultOptionLabel } from '@/utilities/isDefaultOption'
import { getPreselectedVariant } from '@onclicks/sharedmedusa/utils/get-preselected-variant'
import { optionsAsKeymap } from '@onclicks/sharedmedusa/utils/options-as-keymap'
import {
  buildVariantSearchParams,
  resolveOptionsFromSearchParams,
} from '@onclicks/sharedmedusa/utils/get-product-url'
import AddToCartButton from './AddToCartButton'
import ProductPrice from './ProductDisplay/ProductPrice'
import OptionSelect from './ProductDisplay/OptionSelect'

export default function ProductDisplay({ product }: { product: StoreProduct }) {
  const images = useMemo(() => {
    const urls = product.images?.map((image) => image.url).filter(Boolean) ?? []
    if (product.thumbnail && !urls.includes(product.thumbnail)) {
      return [product.thumbnail, ...urls]
    }
    return urls
  }, [product.images, product.thumbnail])

  const [activeImage, setActiveImage] = useState(0)
  const mainImage = images[activeImage] ?? product.thumbnail ?? ''

  const goToPrevImage = () => setActiveImage((index) => (index - 1 + images.length) % images.length)
  const goToNextImage = () => setActiveImage((index) => (index + 1) % images.length)

  const variants = useMemo(() => product.variants ?? [], [product.variants])
  const options = useMemo(() => product.options ?? [], [product.options])

  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>(() => {
    // Si l'URL contient des query params de variante (ex: ?couleur=rouge), on les utilise
    // pour pré-sélectionner les options, pour que l'URL partagée retombe sur la même variante.
    const optionsFromParams = resolveOptionsFromSearchParams(product, searchParams)
    if (Object.keys(optionsFromParams).length > 0) {
      return optionsFromParams
    }

    // Sinon, on retombe sur le comportement par défaut (variante en promo, ou variante par défaut).
    return optionsAsKeymap(getPreselectedVariant(product)?.options)
  })

  const selectedVariant = useMemo(() => {
    if (variants.length === 0) return undefined
    return variants.find((variant) => isEqual(optionsAsKeymap(variant.options), selectedOptions))
  }, [variants, selectedOptions])

  // À chaque changement de sélection d'options, on met à jour l'URL (sans navigation ni scroll)
  // pour qu'elle reflète toujours la variante actuellement affichée et reste partageable telle quelle.
  useEffect(() => {
    const query = buildVariantSearchParams(product, selectedOptions).toString()
    router.replace(`${pathname}${query ? `?${query}` : ''}`, { scroll: false })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOptions])

  const isValidVariant = !!selectedVariant

  const descriptionRef = useRef<HTMLParagraphElement>(null)
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false)
  const [isDescriptionClamped, setIsDescriptionClamped] = useState(false)

  useEffect(() => {
    const el = descriptionRef.current
    if (!el) return
    setIsDescriptionClamped(el.scrollHeight > el.clientHeight)
  }, [product.description])

  const inStock = useMemo(() => {
    if (!selectedVariant) return false
    if (selectedVariant.allow_backorder) return true
    if (!selectedVariant.manage_inventory) return true
    return (selectedVariant.inventory_quantity ?? 0) > 0
  }, [selectedVariant])

  return (
    <div className="px-6 lg:px-16 py-5 lg:py-10 mb-5 lg:mb-10 grid grid-cols-1 lg:grid-cols-2 gap-10 pt-15 lg:pt-28">
      <div>
        <div className="relative overflow-hidden rounded-2xl">
          {mainImage && (
            <img
              src={mainImage}
              alt={product.title}
              className="h-[70vh] w-full object-contain rounded-2xl"
            />
          )}

          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={goToPrevImage}
                aria-label="Image précédente"
                className="absolute left-9 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:bg-white"
              >
                <ChevronLeftIcon className="h-5 w-5 text-primary" />
              </button>
              <button
                type="button"
                onClick={goToNextImage}
                aria-label="Image suivante"
                className="absolute right-9 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 shadow-md transition hover:bg-white"
              >
                <ChevronRightIcon className="h-5 w-5 text-primary" />
              </button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="mt-4 flex flex-wrap gap-3">
            {images.map((image, index) => (
              <button
                key={image}
                type="button"
                onClick={() => setActiveImage(index)}
                className={cn(
                  'h-20 w-20 shrink-0 rounded-xl border-2 transition',
                  activeImage === index
                    ? 'border-primary'
                    : 'border-transparent hover:border-border',
                )}
              >
                <img src={image} alt="" className="h-full w-full object-cover rounded-xl" />
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="pt-2">
        <h1 className={cn('text-3xl font-bold text-primary lg:text-4xl', bagelFatOne.className)}>
          {product.title}
        </h1>

        <ProductPrice product={product} variant={selectedVariant} />

        {product.description && (
          <div className="mt-4">
            <p
              ref={descriptionRef}
              className={cn(
                'leading-relaxed text-muted-foreground whitespace-pre-wrap',
                !isDescriptionExpanded && 'line-clamp-12',
              )}
            >
              {product.description}
            </p>
            {isDescriptionClamped && (
              <button
                type="button"
                onClick={() => setIsDescriptionExpanded((prev) => !prev)}
                className="mt-1 text-sm font-medium text-primary hover:underline"
              >
                {isDescriptionExpanded ? 'Voir moins' : 'Voir plus'}
              </button>
            )}
          </div>
        )}

        {options.length === 1 && isDefaultOptionLabel(options[0]?.title)
          ? null
          : options.map((option) => (
              <OptionSelect
                key={option.id}
                option={option}
                current={selectedOptions[option.id]}
                updateOption={(value) =>
                  setSelectedOptions((prev) => ({ ...prev, [option.id]: value }))
                }
              />
            ))}

        <div className="mt-6">
          <AddToCartButton
            inventoryQuantity={selectedVariant?.inventory_quantity ?? 0}
            variantId={selectedVariant?.id ?? ''}
            countryCode={process.env.NEXT_PUBLIC_MEDUSA_REGION ?? 'be'}
            disabled={!isValidVariant || !inStock}
            unavailableLabel={!isValidVariant ? 'Choisir les variantes' : 'Plus de stock'}
            showStock={isValidVariant}
          />
        </div>
      </div>
    </div>
  )
}
